import React, { useEffect, useState } from "react"
import { Button } from "@mui/material"
import { useLocation, useNavigate } from "react-router-dom"
import { useMutation } from "react-query"
import Swal from "sweetalert2"
import { LocationState } from "../../api/models/cvskill"
import cvskillService from "../../api/services/cvskillService"

interface CheckedItems {
	[key: string]: boolean
}

interface Trait {
	id: number
	personnaliteTrait: string
}

function PolePersonnalite() {
	const [items, setItems] = useState<Trait[]>([]) // liste des traits chargés depuis le back
	const [checkedItems, setCheckedItems] = useState<CheckedItems>({})
	const [error, setError] = useState<string | null>(null)
	const navigate = useNavigate()
	const location = useLocation()
	const state = location.state as LocationState
	const [userId, setUserId] = useState<number | null>(null)
	const [cvSkillId, setCvSkillId] = useState<number | null>(null)
	const { editMode } = state

	// 🆕 Chargement des traits depuis le backend
	useEffect(() => {
		const loadTraits = async () => {
			try {
				const traits = await cvskillService.getPersonnaliteTraits()
				console.log(
					"[LOG FRONT HUGO] Traits récupérés depuis le back :",
					traits,
				)
				setItems(traits)
			} catch (err) {
				console.error(
					"[LOG FRONT HUGO] Erreur chargement traits :",
					err,
				)
				setError("Impossible de charger les traits de personnalité.")
			}
		}
		loadTraits()
	}, [])

	// Chargement des données utilisateur / CV skill
	useEffect(() => {
		const fetchData = async () => {
			try {
				const storedUserId = localStorage.getItem("selectedUserId")
				const storedCvSkillId =
					localStorage.getItem("selectedCvSkillId")

				if (!storedUserId)
					throw new Error("ID utilisateur sélectionné non disponible")

				setUserId(Number(storedUserId))
				if (storedCvSkillId) setCvSkillId(Number(storedCvSkillId))

				// Mode édition → précocher les cases déjà sélectionnées (on utilise les id comme clés string)
				if (editMode && storedCvSkillId) {
					const cvSkill = await cvskillService.getCvSkillById(
						Number(storedCvSkillId),
					)
					if (cvSkill?.polePersonnaliteTraits) {
						const checked = (
							cvSkill.polePersonnaliteTraits as Array<{
								id?: number
							}>
						).reduce((acc: CheckedItems, trait) => {
							if (trait.id == null) return acc // ignore si pas d'id
							acc[String(trait.id)] = true
							return acc
						}, {} as CheckedItems)
						setCheckedItems(checked)
					}
				} else {
					// Mode création → recharger depuis localStorage (on stocke un tableau d'IDs numériques)
					const storedTraits = localStorage.getItem(
						`selectedPersonalityTraits-${storedUserId}`,
					)
					if (storedTraits) {
						try {
							const parsed: number[] = JSON.parse(storedTraits)
							const checked = parsed.reduce(
								(acc: CheckedItems, id: number) => {
									if (id == null) return acc
									acc[String(id)] = true
									return acc
								},
								{} as CheckedItems,
							)
							setCheckedItems(checked)
						} catch (e) {
							console.warn("storedTraits parsing failed", e)
						}
					}
				}
			} catch (error) {
				setError(
					"Impossible de récupérer les données. Veuillez réessayer.",
				)
			}
		}

		fetchData()
	}, [editMode])

	// Gestion de la sélection (3 max)
	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, checked } = event.target // name est une string (id sous forme de string)
		const updatedCheckedItems: CheckedItems = {
			...checkedItems,
			[name]: checked,
		}
		const checkedCount =
			Object.values(updatedCheckedItems).filter(Boolean).length

		if (checkedCount > 3) {
			setError("Vous ne pouvez sélectionner que 3 choix maximum.")
			return
		}

		setCheckedItems(updatedCheckedItems)
		setError(null)

		// Sauvegarde locale (en mode création) : on stocke un tableau de numbers
		if (!editMode && userId) {
			const savedIds = Object.keys(updatedCheckedItems)
				.filter(key => updatedCheckedItems[key])
				.map(Number)
			localStorage.setItem(
				`selectedPersonalityTraits-${userId}`,
				JSON.stringify(savedIds),
			)
		}
	}

	// Mutation pour mise à jour
	const updateCvSkillMutation = useMutation(
		(updatedCvSkill: any) => {
			if (!cvSkillId || !userId)
				throw new Error("CV Skill ID ou User ID invalide")
			return cvskillService.updateCvSkill(
				cvSkillId,
				updatedCvSkill,
				userId,
			)
		},
		{
			onSuccess: () => {
				Swal.fire({
					icon: "success",
					title: "Traits de personnalité mis à jour avec succès",
					showConfirmButton: false,
					timer: 1500,
				})
				navigate("/cvskill/cv", { state: { userId, cvSkillId } })
			},
			onError: () =>
				setError("Erreur lors de la mise à jour. Veuillez réessayer."),
		},
	)

	// Soumission
	const handleSubmit = async () => {
		if (!userId) {
			setError("ID utilisateur manquant. Impossible de continuer.")
			return
		}

		const selectedIds = Object.keys(checkedItems)
			.filter(key => checkedItems[key])
			.map(Number)

		if (selectedIds.length === 0) {
			setError("Veuillez sélectionner au moins un trait de personnalité.")
			return
		}

		const polePersonnaliteTraits = selectedIds
			.map(id => {
				const trait = items.find(t => t.id === id)
				return trait
					? {
							id: trait.id,
							personnaliteTrait: trait.personnaliteTrait,
						}
					: null
			})
			.filter(Boolean) as Array<{ id: number; personnaliteTrait: string }>

		console.log("[LOG FRONT HUGO] 🧠 editMode =", editMode)
		console.log("[LOG FRONT HUGO] 🧾 cvSkillId =", cvSkillId)

		// ✅ CORRECTION: En mode édition ET création, on navigue vers la partie 2
		// On ne met plus à jour directement ici
		if (!editMode && userId) {
			// Sauvegarder en localStorage seulement en mode création
			localStorage.setItem(
				`selectedPersonalityTraits-${userId}`,
				JSON.stringify(selectedIds),
			)
			console.log(
				"[LOG FRONT HUGO] ✅ Traits sauvegardés localement localStorage:",
				selectedIds,
			)
		}

		console.log(
			"[LOG FRONT HUGO] 🚀 Navigation vers page suivante avec state :",
			{
				userId,
				cvSkillId,
				polePersonnaliteTraits,
				editMode, // Important de passer editMode au state
			},
		)

		// Navigation vers la partie 2 (que ce soit en édition ou création)
		navigate("/cvskill/polePersonnalite2", {
			state: {
				...state,
				userId,
				cvSkillId,
				polePersonnaliteTraits,
				editMode, // ✅ Passer editMode pour que la partie 2 sache qu'on est en édition
			},
		})
	}

	const selectedCount = Object.values(checkedItems).filter(Boolean).length

	// Rendu
	return (
		<div className="container mx-auto p-4 max-w-3xl relative overflow-y-auto h-[calc(100vh-8rem)] pb-16">
			<style>{`
                .container::-webkit-scrollbar {
                    display: none;
                }
                .container {
                    scrollbar-width: none;
                    -ms-overflow-style: none;
                }
                
                .page-header {
                    background: linear-gradient(135deg, #2daf8e 0%, #238f74 100%);
                    color: white;
                    padding: 20px 24px;
                    border-radius: 16px;
                    text-align: center;
                    box-shadow: 0 4px 12px rgba(45, 175, 142, 0.3);
                    margin-bottom: 24px;
                }
                
                .page-header h2 {
                    font-size: 24px;
                    font-weight: 700;
                    margin: 0;
                }
                
                .part-badge {
                    display: inline-block;
                    background: white;
                    color: #2daf8e;
                    padding: 8px 16px;
                    border-radius: 20px;
                    font-weight: 600;
                    font-size: 14px;
                    margin-top: 12px;
                }
                
                .selection-counter {
                    background: linear-gradient(135deg, #e0f7f3 0%, #b8ede0 100%);
                    color: #2daf8e;
                    padding: 12px 20px;
                    border-radius: 12px;
                    font-weight: 600;
                    font-size: 16px;
                    text-align: center;
                    margin-bottom: 16px;
                    box-shadow: 0 2px 8px rgba(45, 175, 142, 0.2);
                }
                
                .selection-card {
                    background: white;
                    border: 2px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 16px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                }
                
                .selection-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(45, 175, 142, 0.2);
                    border-color: #7dd4c1;
                }
                
                .selection-card.selected {
                    background: linear-gradient(135deg, #d4f5ed 0%, #b8ede0 100%);
                    border-color: #2daf8e;
                    box-shadow: 0 4px 12px rgba(45, 175, 142, 0.3);
                }
                
                .selection-checkbox {
                    width: 20px;
                    height: 20px;
                    border-radius: 4px;
                    border: 2px solid #d1d5db;
                    cursor: pointer;
                    accent-color: #2daf8e;
                }
                
                .selection-checkbox:checked {
                    background-color: #2daf8e;
                    border-color: #2daf8e;
                }
                
                .action-button {
                    background: linear-gradient(135deg, #2daf8e 0%, #238f74 100%) !important;
                    box-shadow: 0 4px 12px rgba(45, 175, 142, 0.3) !important;
                    text-transform: none !important;
                    font-size: 16px !important;
                    font-weight: 600 !important;
                    padding: 12px 24px !important;
                    border-radius: 12px !important;
                    width: 100%;
                    transition: all 0.3s ease !important;
                }
                
                .action-button:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(45, 175, 142, 0.4) !important;
                }
                
                .action-button:disabled {
                    background: #d1d5db !important;
                    box-shadow: none !important;
                    cursor: not-allowed;
                }
            `}</style>

			<div className="page-header">
				<h2>
					{editMode
						? "Modifier les traits de personnalité"
						: "Pôle Personnalité"}
				</h2>
				<div className="part-badge">Partie 1 sur 2</div>
			</div>

			{error && (
				<p className="text-red-500 text-center bg-red-50 p-3 rounded-lg mb-4">
					⚠️ {error}
				</p>
			)}

			<div className="selection-counter">
				{selectedCount === 3 ? "✓" : "📊"} {selectedCount} / 3 traits
				sélectionnés
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
				{items.map(item => {
					const isSelected = checkedItems[item.id.toString()] || false
					return (
						<div
							key={item.id}
							className={`selection-card ${isSelected ? "selected" : ""}`}
							onClick={() =>
								handleChange({
									target: {
										name: item.id.toString(),
										checked: !isSelected,
									},
								} as React.ChangeEvent<HTMLInputElement>)
							}
						>
							<label
								htmlFor={item.id.toString()}
								className="flex items-center space-x-3 cursor-pointer"
							>
								<input
									type="checkbox"
									id={item.id.toString()}
									name={item.id.toString()}
									checked={isSelected}
									onChange={() => {}}
									className="selection-checkbox"
								/>
								<span className="text-gray-800 font-medium text-base flex-1">
									{item.personnaliteTrait}
								</span>
							</label>
						</div>
					)
				})}
			</div>

			<p className="mt-6 font-bold text-center text-red-500 bg-red-50 p-3 rounded-lg">
				⚠️ 3 choix maximum
			</p>

			<div className="flex justify-center mt-8">
				<Button
					onClick={handleSubmit}
					variant="contained"
					className="action-button"
					disabled={selectedCount === 0 || selectedCount > 3}
				>
					Continuer →{" "}
					{/* ✅ Toujours "Continuer" car on va vers la partie 2 */}
				</Button>
			</div>
		</div>
	)
}

export default PolePersonnalite
